import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Search,
  Filter,
  Clock,
  Award,
  BarChart3,
  ChevronRight,
  BookOpen,
  Target,
  Trophy,
  Star,
  Plus,
  Loader2,
  CheckCircle
} from "lucide-react";

const DIFFICULTY_CONFIG = {
  beginner: { label: "Beginner", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  intermediate: { label: "Intermediate", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  advanced: { label: "Advanced", color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
};

const AssessmentsBrowser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [assessRes, catRes, resultsRes, badgesRes] = await Promise.all([
        fetch(`${API}/assessments`, { credentials: "include", headers }),
        fetch(`${API}/assessments/categories`, { credentials: "include", headers }),
        user ? fetch(`${API}/assessments/my-results`, { credentials: "include", headers }) : Promise.resolve(null),
        user ? fetch(`${API}/assessments/my-badges`, { credentials: "include", headers }) : Promise.resolve(null),
      ]);

      if (assessRes.ok) setAssessments(await assessRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (resultsRes?.ok) setMyResults(await resultsRes.json());
      if (badgesRes?.ok) setMyBadges(await badgesRes.json());
    } catch (err) {
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  const filteredAssessments = assessments.filter((a) => {
    if (selectedCategory && a.category !== selectedCategory) return false;
    if (selectedDifficulty && a.difficulty !== selectedDifficulty) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        a.title?.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q) ||
        a.skill_tags?.some((t) => t.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const getCompletionStatus = (assessmentId) => {
    const result = myResults.find((r) => r.assessment_id === assessmentId);
    if (!result) return null;
    return result;
  };

  const hasBadge = (assessmentId) => myBadges.some((b) => b.assessment_id === assessmentId);

  const totalPassed = myResults.filter((r) => r.passed).length;
  const totalBadges = myBadges.length;
  const avgScore = myResults.length > 0 ? Math.round(myResults.reduce((s, r) => s + r.score, 0) / myResults.length) : 0;

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="assessments-title">
                <ClipboardCheck className="w-7 h-7 text-crimson" />
                Skills Assessments
              </h1>
              <p className="text-gray-400 mt-1 text-sm">Test your skills and earn verified badges</p>
            </div>
            {user?.role === "recruiter" && (
              <Button
                onClick={() => navigate("/assessments/create")}
                className="bg-crimson hover:bg-crimson/90 text-white"
                data-testid="create-assessment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Assessment
              </Button>
            )}
          </div>

          {/* Stats Row */}
          {user && myResults.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Completed", value: myResults.length, icon: CheckCircle, color: "text-blue-400" },
                { label: "Passed", value: totalPassed, icon: Trophy, color: "text-emerald-400" },
                { label: "Badges", value: totalBadges, icon: Award, color: "text-amber-400" },
                { label: "Avg Score", value: `${avgScore}%`, icon: Target, color: "text-crimson" },
              ].map((s) => (
                <div key={s.label} className="bg-obsidian-light border border-[#222] p-4 rounded" data-testid={`stat-${s.label.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className={`w-4 h-4 ${s.color}`} />
                    <span className="text-xs text-gray-400">{s.label}</span>
                  </div>
                  <p className="text-xl font-bold">{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search assessments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-obsidian-light border border-[#333] rounded pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                data-testid="search-assessments"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-obsidian-light border border-[#333] rounded px-4 py-2.5 text-sm text-white focus:border-crimson focus:outline-none"
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="bg-obsidian-light border border-[#333] rounded px-4 py-2.5 text-sm text-white focus:border-crimson focus:outline-none"
              data-testid="filter-difficulty"
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Assessment Cards */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : filteredAssessments.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No assessments found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredAssessments.map((assessment, idx) => {
                const completion = getCompletionStatus(assessment.assessment_id);
                const badge = hasBadge(assessment.assessment_id);
                const diff = DIFFICULTY_CONFIG[assessment.difficulty] || DIFFICULTY_CONFIG.intermediate;

                return (
                  <motion.div
                    key={assessment.assessment_id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="bg-obsidian-light border border-[#222] hover:border-[#444] rounded-lg overflow-hidden transition-colors group cursor-pointer"
                    onClick={() => navigate(`/assessments/${assessment.assessment_id}`)}
                    data-testid={`assessment-card-${assessment.assessment_id}`}
                  >
                    {/* Category accent bar */}
                    <div className="h-1 bg-gradient-to-r from-crimson to-crimson/40" />

                    <div className="p-5 space-y-3">
                      {/* Top row: title + badge */}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-base group-hover:text-crimson transition-colors line-clamp-2">
                          {assessment.title}
                        </h3>
                        {badge && (
                          <div className="flex-shrink-0 bg-amber-500/10 border border-amber-500/30 rounded-full p-1.5" title="Badge Earned">
                            <Award className="w-4 h-4 text-amber-400" />
                          </div>
                        )}
                      </div>

                      <p className="text-gray-400 text-sm line-clamp-2">{assessment.description}</p>

                      {/* Tags row */}
                      <div className="flex flex-wrap gap-1.5">
                        {assessment.skill_tags?.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-white/5 border border-[#333] rounded text-xs text-gray-300">
                            {tag}
                          </span>
                        ))}
                        {assessment.skill_tags?.length > 3 && (
                          <span className="px-2 py-0.5 text-xs text-gray-500">+{assessment.skill_tags.length - 3}</span>
                        )}
                      </div>

                      {/* Meta row */}
                      <div className="flex items-center gap-3 text-xs text-gray-500 pt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {assessment.time_limit} min
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3.5 h-3.5" />
                          {assessment.question_count || assessment.questions?.length || "?"} Q
                        </span>
                        <span className={`px-2 py-0.5 rounded border text-xs ${diff.bg} ${diff.color}`}>
                          {diff.label}
                        </span>
                        <span className="ml-auto px-2 py-0.5 bg-white/5 border border-[#333] rounded text-gray-400">
                          {assessment.category}
                        </span>
                      </div>

                      {/* Completion bar */}
                      {completion && (
                        <div className="pt-2 border-t border-[#222] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {completion.passed ? (
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <Target className="w-4 h-4 text-amber-400" />
                            )}
                            <span className={`text-sm font-medium ${completion.passed ? "text-emerald-400" : "text-amber-400"}`}>
                              {completion.score}%
                            </span>
                            <span className="text-xs text-gray-500">
                              {completion.passed ? "Passed" : "Try Again"}
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-crimson transition-colors" />
                        </div>
                      )}

                      {!completion && (
                        <div className="pt-2 border-t border-[#222] flex items-center justify-between">
                          <span className="text-xs text-gray-500">Not attempted</span>
                          <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-crimson transition-colors" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AssessmentsBrowser;
